import { decodeJwt } from 'jose';
import { env } from '$env/dynamic/public';

import type { User } from '$lib/interfaces/interfaces';

export interface ProspectToken {
  investor_id: string;
  investor_type: string;
  email: string;
  name: string;
  org_slug: string;
  iss: string;
  sub: string;
  aud: string;
  iat: number;
  nbf: number;
  exp: number;
}

export interface UserToken {
  admin: boolean;
  doc_admin: boolean;
  email: string;
  exp: number;
  fundmanager: boolean;
  jwt_token: string;
  name: string;
  sub: string;
  super_admin: boolean;
  user_id: string;
}

// handles investor.<domain> and <domain>
export function deleteCookie(cookieName: string) {
  const hostname = window.location.hostname;
  const path = 'path=/';
  const expires = 'expires=Thu, 01 Jan 1970 00:00:00 UTC';

  // 1. Try deleting on the exact hostname (and default implicit domain)
  document.cookie = `${cookieName}=; ${expires}; ${path};`;
  document.cookie = `${cookieName}=; ${expires}; ${path}; domain=${hostname};`;

  // 2. Try deleting on the root domain (e.g., .flowinc.app)
  const domainParts = hostname.split('.');
  if (domainParts.length > 1) {
    // Grabs the last two parts of the domain (flowinc.app) and prepends a dot
    const rootDomain = `.${domainParts.slice(-2).join('.')}`;
    document.cookie = `${cookieName}=; ${expires}; ${path}; domain=${rootDomain};`;
  }
}

/**
 * Retrieves a cookie by name.
 */
export function getCookie(name: string): string | undefined {
  if (typeof document === 'undefined') return;

  const match = document.cookie.match(new RegExp('(^| )' + name + '=([^;]+)'));

  if (match) return decodeURIComponent(match[2]);
}

export function setCookie(token: string, name: string, rootDomain?: string): void {
  if (typeof document === 'undefined') return;

  let expiresStr = '';
  let domainStr = '';

  try {
    const decoded: ProspectToken = decodeJwt(token);

    if (decoded.exp) {
      const expiresDate = new Date(decoded.exp * 1000);
      expiresStr = `; expires=${expiresDate.toUTCString()}`;
    }
  } catch (error) {
    console.error('Failed to parse JWT for cookie expiration:', error);
  }

  // If a root domain is provided, format it into the cookie string
  if (rootDomain) domainStr = `; domain=${rootDomain}`;

  document.cookie = `${name}=${encodeURIComponent(token)}${expiresStr}${domainStr}; path=/; SameSite=Strict`;
}

// --- Impersonation session capabilities ----------------------------------------------
// The server decides whether an impersonation session is locked to its organization
// (GP impersonators are; Flow admins are not) and serializes the verdict on user_info.
// These predicates only consume that verdict — never re-derive the rule client-side.

export function impersonationScoped(userInfo: User | null | undefined): boolean {
  return !!userInfo?.impersonation_scoped;
}

export function canBrowsePortalRoot(userInfo: User | null | undefined): boolean {
  return !impersonationScoped(userInfo);
}

/**
 * Data rooms resolve by the bearer token's identity, so under impersonation they would show
 * the impersonator's own rooms, not the target investor's. Hidden for every impersonated
 * session — admins included — for the initial release.
 */
export function canBrowseDataRooms(userInfo: User | null | undefined): boolean {
  return !userInfo?.impersonated;
}

/**
 * Where a scoped impersonation session belongs — its organization's landing page.
 * Undefined for unscoped sessions (regular users, admin impersonators).
 */
export function impersonationHomePath(userInfo: User | null | undefined, url?: URL): string | undefined {
  if (!impersonationScoped(userInfo) || !userInfo?.impersonation_organization_slug) return undefined;

  const slug = userInfo.impersonation_organization_slug;

  // Detect if the app is currently being served under the /fli path
  const basePath = url?.pathname.startsWith('/fli/') || url?.pathname === '/fli' ? '/fli' : '';

  return `${basePath}/g/${slug}`;
}

export function isApexGroupEmail(value: string): boolean {
  if (!value) return false;

  return buildApexGroupEmailRegex().test(value);
}

function buildApexGroupEmailRegex(): RegExp {
  // return regex that matches nothing if no domains are configured
  if (!env.PUBLIC_APEX_SSO_DOMAINS) return /(?!)/;

  const domainSet = new Set<string>();

  env.PUBLIC_APEX_SSO_DOMAINS.split(',').forEach((domain) => {
    const trimmed = domain.trim();
    if (trimmed && /^[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/.test(trimmed)) {
      domainSet.add(trimmed);
    }
  });

  const domains = Array.from(domainSet);
  const escapedDomains = domains.map((d) => d.replace(/\./g, '\\.'));

  // edge case if none of the strings were valid domains
  if (escapedDomains.length === 0) {
    return /(?!)/;
  }

  return new RegExp(`^[^@\\s]+@(${escapedDomains.join('|')})$`, 'i');
}
